import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { useUserData } from '../auth';

export interface WhatsAppSessionConfig {
  id: string;
  serviceName: 'waha' | 'whatsapp_cloud' | 'whatsapp_cloud_coex';
  displayName: string | null;
  phoneNumber: string;
  session: string;
  visibility: 'global' | 'personal';
  userId: string | null;
  ownerName: string;
  healthStatus: string;
  isActive: boolean;
}

/**
 * Cache di modulo per utente+org: il hook e' montato in piu' punti insieme
 * (drawer, scheda lead, intestazione, tabella, kanban) e ognuno faceva le sue
 * tre query. Una sola richiesta in volo per chiave, risultato buono un minuto;
 * `refetch` salta la cache.
 */
const CACHE_TTL_MS = 60_000;
const cache = new Map<string, { at: number; data: WhatsAppSessionConfig[] }>();
const inVolo = new Map<string, Promise<WhatsAppSessionConfig[]>>();

async function caricaConfigs(orgId: string, userId: string): Promise<WhatsAppSessionConfig[]> {
  // 1. Fetch all active WhatsApp configs for the org (WAHA + Cloud Standalone + Cloud Coex)
  const { data: configsData, error: configsError } = await supabase
    .from('api_configurations')
    .select('id, service_name, configuration, is_active, created_at, user_id, session_health_status, visibility, display_name')
    .in('service_name', ['waha', 'whatsapp_cloud', 'whatsapp_cloud_coex'])
    .eq('organization_id', orgId)
    .eq('is_active', true);

  if (configsError) throw configsError;
  if (!configsData || configsData.length === 0) return [];

  // 2. Fetch user's access rows
  const { data: accessData } = await supabase
    .from('whatsapp_user_access')
    .select('configuration_id')
    .eq('user_id', userId)
    .eq('organization_id', orgId);

  const accessibleConfigIds = new Set((accessData || []).map(a => a.configuration_id));

  // 2b. Per config globali: conta quanti accessi totali ci sono (per sapere se ci sono restrizioni)
  const globalConfigIds = configsData
    .filter(c => (c.visibility || 'global') === 'global' || !c.user_id)
    .map(c => c.id);

  let configsWithRestrictions = new Set<string>();
  if (globalConfigIds.length > 0) {
    const { data: allAccessData } = await supabase
      .from('whatsapp_user_access')
      .select('configuration_id')
      .in('configuration_id', globalConfigIds)
      .eq('organization_id', orgId);

    // Se una config ha almeno 1 riga in whatsapp_user_access, ha restrizioni attive
    if (allAccessData) {
      configsWithRestrictions = new Set(allAccessData.map(a => a.configuration_id));
    }
  }

  // 3. Filter: show configs where user has access
  const filteredConfigs = configsData.filter(c => {
    const isMyPersonalConfig = c.user_id === userId;
    const isGlobal = (c.visibility || 'global') === 'global' || !c.user_id;
    const hasAccessRow = accessibleConfigIds.has(c.id);
    const hasRestrictions = configsWithRestrictions.has(c.id);

    // Always show my personal configs
    if (isMyPersonalConfig) return true;
    // Global config con restrizioni: mostra solo se l'utente ha accesso
    if (isGlobal && hasRestrictions) return hasAccessRow;
    // Global config senza restrizioni: visibile a tutti
    if (isGlobal && !hasRestrictions) return true;

    return false;
  });

  // Filter: WAHA richiede session + phone_number in configuration; i Cloud non li hanno
  // (i metadati Cloud vivono in colonne dedicate) → sempre validi se attivi.
  const validConfigs = filteredConfigs.filter(c =>
    c.service_name === 'waha'
      ? !!(c.configuration?.session && c.configuration?.phone_number)
      : true
  );

  // 4. Join owner names from user_profiles
  const ownerIds = validConfigs
    .map(c => c.user_id)
    .filter((id): id is string => id !== null);

  let userProfiles: Record<string, { full_name: string }> = {};
  if (ownerIds.length > 0) {
    const { data: profiles } = await supabase
      .from('user_profiles')
      .select('id, full_name')
      .in('id', [...new Set(ownerIds)]);

    if (profiles) {
      userProfiles = profiles.reduce((acc, p) => {
        acc[p.id] = p;
        return acc;
      }, {} as Record<string, any>);
    }
  }

  // 5. Map to WhatsAppSessionConfig
  return validConfigs.map(c => {
    const ownerProfile = c.user_id ? userProfiles[c.user_id] : null;
    const isWaha = c.service_name === 'waha';
    return {
      id: c.id,
      serviceName: c.service_name as WhatsAppSessionConfig['serviceName'],
      displayName: c.display_name || null,
      // WAHA: configuration.phone_number; Cloud: configuration.display_phone_number (dal cron).
      phoneNumber: isWaha ? c.configuration.phone_number : (c.configuration?.display_phone_number || ''),
      session: isWaha ? c.configuration.session : '',
      visibility: (c.visibility || 'global') as 'global' | 'personal',
      userId: c.user_id,
      ownerName: ownerProfile
        ? ownerProfile.full_name || 'Utente'
        : 'Organizzazione',
      healthStatus: c.session_health_status || 'unknown',
      isActive: c.is_active,
    };
  });
}

function caricaConCache(orgId: string, userId: string, force: boolean): Promise<WhatsAppSessionConfig[]> {
  const key = `${orgId}:${userId}`;
  const hit = cache.get(key);
  if (!force && hit && Date.now() - hit.at < CACHE_TTL_MS) return Promise.resolve(hit.data);
  const pendente = inVolo.get(key);
  if (pendente) return pendente;
  const p = caricaConfigs(orgId, userId)
    .then((data) => {
      cache.set(key, { at: Date.now(), data });
      return data;
    })
    .finally(() => inVolo.delete(key));
  inVolo.set(key, p);
  return p;
}

export function useWhatsAppConfigs() {
  const { organizationId: orgId, userProfile } = useUserData();
  const userId = userProfile?.id;
  const key = orgId && userId ? `${orgId}:${userId}` : null;
  const hit = key ? cache.get(key) : undefined;
  const [configs, setConfigs] = useState<WhatsAppSessionConfig[]>(hit?.data ?? []);
  const [loading, setLoading] = useState(!hit);
  const [error, setError] = useState<string | null>(null);

  const fetchConfigs = useCallback(async (force = false) => {
    if (!userId || !orgId) {
      setConfigs([]);
      setLoading(false);
      return;
    }
    const fresco = !force && cache.get(`${orgId}:${userId}`);
    if (!fresco) setLoading(true);
    setError(null);
    try {
      setConfigs(await caricaConCache(orgId, userId, force));
    } catch (err: any) {
      console.error('[useWhatsAppConfigs] Error:', err);
      setError(err.message || 'Errore nel caricamento configurazioni WhatsApp');
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [userId, orgId]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const refetch = useCallback(() => fetchConfigs(true), [fetchConfigs]);

  return { configs, loading, error, refetch };
}
